import axiosInstance from "../../../util/axiosInstance";
import { getToken } from "../../../util/Cookies";

/**
 * @typedef {Object} CuisineMaster
 * @property {number} id
 * @property {string} name
 * @property {string} [description]
 */

/**
 * @typedef {Object} CateringPackage
 * @property {number} itemId
 * @property {string} customComboName
 * @property {string} [description]
 * @property {number} mealId
 * @property {string} mealName
 * @property {number} regularPrice
 * @property {number|null} [discountedPrice]
 * @property {number|null} [cuisineMasterId]
 * @property {CuisineMaster|null} [cuisineMaster]
 */

const authenticatedJsonConfig = (config = {}) => {
  const token = getToken();
  const bearerToken = token
    ? token.startsWith("Bearer ") ? token : `Bearer ${token}`
    : "";

  return {
    ...config,
    headers: {
      "Content-Type": "application/json",
      ...(bearerToken ? { Authorization: bearerToken } : {}),
      ...config.headers,
    },
  };
};

export const getApiErrorMessage = (error, fallbackMessage) => {
  if (error?.code === "ERR_CANCELED") return "Request cancelled";

  const responseData = error?.response?.data;
  return (
    responseData?.message ||
    responseData?.error?.message ||
    (typeof responseData?.error === "string" ? responseData.error : null) ||
    error?.message ||
    fallbackMessage
  );
};

export const normalizeCuisineMaster = (cuisine) => {
  const id = Number(cuisine?.id ?? cuisine?.ID ?? cuisine?.mealId ?? cuisine?.meal_id ?? cuisine?.type_id);

  return {
    id,
    name: String(cuisine?.name ?? cuisine?.mealName ?? cuisine?.meal_name ?? cuisine?.label ?? cuisine?.type_name ?? ""),
    description: String(cuisine?.description ?? cuisine?.desc ?? ""),
  };
};

const optionalNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const serializeValidUntil = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new Error("Invalid offer expiry date. Please choose the date again.");
    }
    return value.toISOString();
  }

  const dateText = String(value).trim();
  const displayDateMatch = dateText.match(/^(\d{2})\/(\d{2})\/(\d{2}|\d{4})$/);

  if (displayDateMatch) {
    const [, dayText, monthText, yearText] = displayDateMatch;
    const day = Number(dayText);
    const month = Number(monthText);
    const year = yearText.length === 2 ? 2000 + Number(yearText) : Number(yearText);
    const parsedDate = new Date(year, month - 1, day);

    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      throw new Error("Invalid offer expiry date. Please choose the date again.");
    }

    return parsedDate.toISOString();
  }

  const parsedDate = new Date(dateText);
  if (Number.isNaN(parsedDate.getTime())) {
    throw new Error("Invalid offer expiry date. Please choose the date again.");
  }

  return parsedDate.toISOString();
};

const serializeCateringItem = (cateringItem) => ({
  ...cateringItem,
  valid_until: serializeValidUntil(cateringItem?.valid_until),
});

export const normalizeCateringPackage = (cateringPackage) => {
  const rawCuisine =
    cateringPackage?.catering_package_cuisine_master ??
    cateringPackage?.cuisine_master ??
    cateringPackage?.cuisineMaster ??
    null;
  const cuisineMaster = rawCuisine ? normalizeCuisineMaster(rawCuisine) : null;
  const cuisineMasterId = optionalNumber(
    cateringPackage?.catering_package_cuisine_master_id ??
      cateringPackage?.cuisine_master_id ??
      cateringPackage?.cuisineMasterId ??
      cuisineMaster?.id
  );
  const itemId = optionalNumber(cateringPackage?.item_id ?? cateringPackage?.itemId);
  const mealId = optionalNumber(
    cateringPackage?.meal?.meal_id ?? cateringPackage?.meal_id ?? cateringPackage?.mealId
  );
  const mealName = String(
    cateringPackage?.meal?.meal_name ?? cateringPackage?.meal_name ?? cateringPackage?.mealName ?? ""
  );
  const regularPrice = optionalNumber(
    cateringPackage?.regular_price ?? cateringPackage?.regularPrice
  ) ?? 0;
  const discountedPrice = optionalNumber(
    cateringPackage?.discounted_price ?? cateringPackage?.discountedPrice
  );

  return {
    ...cateringPackage,
    itemId,
    item_id: itemId,
    customComboName: String(
      cateringPackage?.custom_combo_name ?? cateringPackage?.customComboName ?? ""
    ),
    custom_combo_name: String(
      cateringPackage?.custom_combo_name ?? cateringPackage?.customComboName ?? ""
    ),
    mealId,
    mealName,
    meal: {
      ...(cateringPackage?.meal || {}),
      meal_id: mealId,
      meal_name: mealName,
    },
    regularPrice,
    regular_price: regularPrice,
    discountedPrice,
    discounted_price: discountedPrice,
    cuisineMasterId,
    cuisineMaster,
    catering_package_cuisine_master_id: cuisineMasterId,
    catering_package_cuisine_master: cuisineMaster,
  };
};

export const fetchCuisineMasters = async (mealId, { signal } = {}) => {
  const normalizedMealId = Number(mealId);
  console.log(`fetchCuisineMasters called with mealId: ${mealId} -> ${normalizedMealId}`);
  
  if (!Number.isFinite(normalizedMealId) || normalizedMealId <= 0) {
    console.log("fetchCuisineMasters returning empty because mealId is invalid.");
    return [];
  }

  try {
    const url = `/catering-items/cuisine-masters?mealId=${normalizedMealId}`;
    const response = await axiosInstance.get(
      url,
      authenticatedJsonConfig({ signal })
    );
    const cuisines = Array.isArray(response.data) ? response.data : response.data?.data;
    console.log(`fetchCuisineMasters API response for /cuisine/cuisine-types:`, cuisines);

    if (!Array.isArray(cuisines)) return [];


    return cuisines
      .map(normalizeCuisineMaster)
      .filter((cuisine) => Number.isFinite(cuisine.id));
  } catch (error) {
    if (error?.code === "ERR_CANCELED") throw error;
    throw new Error(getApiErrorMessage(error, "Unable to load cuisines."));
  }
};

export const createCateringPackage = async (cateringItem) => {
  try {
    const response = await axiosInstance.post(
      "/catering-items",
      { cateringItems: [serializeCateringItem(cateringItem)] },
      authenticatedJsonConfig()
    );

    if (response.status !== 200 && response.status !== 201) {
      throw new Error("The package could not be created.");
    }

    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to create the catering package."));
  }
};

export const updateCateringPackage = async (cateringItem) => {
  try {
    const response = await axiosInstance.put(
      "/catering-items",
      { cateringItems: [serializeCateringItem(cateringItem)] },
      authenticatedJsonConfig()
    );

    if (response.status !== 200 && response.status !== 201) {
      throw new Error("The package could not be updated.");
    }

    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to update the catering package."));
  }
};

export const fetchCateringPackages = async () => {
  try {
    const response = await axiosInstance.get(
      "/catering-items",
      authenticatedJsonConfig()
    );
    const packages = Array.isArray(response.data) ? response.data : response.data?.data;

    if (!Array.isArray(packages)) return [];
    return packages.map(normalizeCateringPackage);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to load catering packages."));
  }
};
